export const AGENT_MARK_VIEWBOX_SIZE: number = 40;

export const AGENT_MARK_TRIANGLE_VERTICES: readonly [number, number][] = [
  [20, 3],
  [37.5, 35.5],
  [2.5, 35.5],
];

/** Crop to triangle vertex bounds so the mark fills its px box (no inner padding). */
export const AGENT_MARK_TIGHT_VIEWBOX: string = "2.5 3 35 32.5";

/** Centroid Y of {@link AGENT_MARK_TRIANGLE_VERTICES} in tight-viewBox space (percent). */
export const AGENT_MARK_LETTER_CENTER_Y_PERCENT: number =
  (((AGENT_MARK_TRIANGLE_VERTICES[0]![1] +
    AGENT_MARK_TRIANGLE_VERTICES[1]![1] +
    AGENT_MARK_TRIANGLE_VERTICES[2]![1]) /
    3 -
    3) /
    32.5) *
  100;

function hypot(dx: number, dy: number): number {
  return Math.hypot(dx, dy);
}

function maxFilletRadiusAtVertex(
  prev: [number, number],
  curr: [number, number],
  next: [number, number],
): number {
  const lenIn: number = hypot(curr[0] - prev[0], curr[1] - prev[1]);
  const lenOut: number = hypot(next[0] - curr[0], next[1] - curr[1]);
  const inX: number = prev[0] - curr[0];
  const inY: number = prev[1] - curr[1];
  const outX: number = next[0] - curr[0];
  const outY: number = next[1] - curr[1];
  const dot: number =
    (inX * outX + inY * outY) / Math.max(lenIn * lenOut, 1e-6);
  const interiorAngle: number = Math.acos(Math.max(-1, Math.min(1, dot)));
  const halfAngle: number = interiorAngle / 2;
  const tanHalf: number = Math.tan(halfAngle);
  if (tanHalf < 1e-6) return 0;
  return Math.min(lenIn, lenOut) * tanHalf * 0.45;
}

function buildRoundedTrianglePath(
  vertices: readonly [number, number][],
  radiusViewBox: number,
): string {
  const n: number = vertices.length;
  const parts: string[] = [];

  for (let i = 0; i < n; i++) {
    const prev: [number, number] = vertices[(i - 1 + n) % n]!;
    const curr: [number, number] = vertices[i]!;
    const next: [number, number] = vertices[(i + 1) % n]!;

    const radius: number = Math.min(
      radiusViewBox,
      maxFilletRadiusAtVertex(prev, curr, next),
    );
    if (radius < 1e-4) continue;

    const lenIn: number = hypot(curr[0] - prev[0], curr[1] - prev[1]);
    const lenOut: number = hypot(next[0] - curr[0], next[1] - curr[1]);
    const inX: number = (prev[0] - curr[0]) / lenIn;
    const inY: number = (prev[1] - curr[1]) / lenIn;
    const outX: number = (next[0] - curr[0]) / lenOut;
    const outY: number = (next[1] - curr[1]) / lenOut;
    const dot: number = inX * outX + inY * outY;
    const interiorAngle: number = Math.acos(Math.max(-1, Math.min(1, dot)));
    const offset: number = radius / Math.tan(interiorAngle / 2);

    const startX: number = curr[0] + inX * offset;
    const startY: number = curr[1] + inY * offset;
    const endX: number = curr[0] + outX * offset;
    const endY: number = curr[1] + outY * offset;

    if (parts.length === 0) parts.push(`M ${startX} ${startY}`);
    else parts.push(`L ${startX} ${startY}`);
    parts.push(`A ${radius} ${radius} 0 0 1 ${endX} ${endY}`);
  }

  parts.push("Z");
  return parts.join(" ");
}

function cornerRadiusInViewBox(
  renderSizePx: number,
  cornerRadiusPx: number,
): number {
  return cornerRadiusPx * (AGENT_MARK_VIEWBOX_SIZE / renderSizePx);
}

/** Agents card avatar: 2 px fillet at 50 px. Corners scale with size — small marks stay sharp. */
const AGENT_MARK_CORNER_RADIUS_RATIO: number = 2 / 50;

/** Only for {@link AGENT_MARK_PATH}: a fixed px radius would round small marks too much. */
function buildAgentRoundedTrianglePath(
  renderSizePx: number,
  cornerRadiusPx: number,
): string {
  return buildRoundedTrianglePath(
    AGENT_MARK_TRIANGLE_VERTICES,
    cornerRadiusInViewBox(renderSizePx, cornerRadiusPx),
  );
}

/** The one agent triangle (tight viewBox): same shape at every size. */
export const AGENT_MARK_PATH: string = buildAgentRoundedTrianglePath(
  AGENT_MARK_VIEWBOX_SIZE,
  AGENT_MARK_VIEWBOX_SIZE * AGENT_MARK_CORNER_RADIUS_RATIO,
);

/** Brand logo tile + triangle geometry (512×512, {@link BrandLogoMark}). */
export const BRAND_LOGO_VIEWBOX_SIZE: number = 512;

export const BRAND_LOGO_TILE_RX: number = 112;

/** Screen-space fillet for brand triangle in {@link BrandLogoMark} and favicon PNGs. */
export const BRAND_TRIANGLE_MARK_CORNER_RADIUS_PX: number = 1.5;

/** Brand mark triangle control points (512-space). */
export const BRAND_LOGO_TRIANGLE_VERTICES: readonly [number, number][] = [
  [256, 148],
  [372, 364],
  [140, 364],
];

/** Sharp polygon points string for brand logo SVG (`loading-mark-agents.svg`). */
export const BRAND_LOGO_TRIANGLE_POLYGON_POINTS: string =
  "256,148 372,364 140,364";

/** Fillet radius in 512-space — matches rounded brand PNG asset, not sharp polygon. */
export const BRAND_LOGO_TRIANGLE_CORNER_RADIUS: number = 14;

/** Rounded brand triangle path for logo marks (tile + mark SVG). */
export function buildBrandLogoTrianglePath(): string {
  return buildRoundedTrianglePath(
    BRAND_LOGO_TRIANGLE_VERTICES,
    BRAND_LOGO_TRIANGLE_CORNER_RADIUS,
  );
}

/** Width of {@link BRAND_LOGO_TRIANGLE_TIGHT_VIEWBOX} content (512-space units). */
export const BRAND_LOGO_TRIANGLE_TIGHT_WIDTH: number = 232;

function brandLogoCornerRadiusInViewBox(
  renderSizePx: number,
  cornerRadiusPx: number,
): number {
  return cornerRadiusPx * (BRAND_LOGO_TRIANGLE_TIGHT_WIDTH / renderSizePx);
}

/** Brand triangle path with fixed screen-space corner radius (SVG arc fillets). */
export function buildBrandLogoRoundedTrianglePath(
  renderSizePx: number,
  cornerRadiusPx: number,
): string {
  return buildRoundedTrianglePath(
    BRAND_LOGO_TRIANGLE_VERTICES,
    brandLogoCornerRadiusInViewBox(renderSizePx, cornerRadiusPx),
  );
}

/** Crop to {@link BRAND_LOGO_TRIANGLE_VERTICES} bounds so the mark fills its px box. */
export const BRAND_LOGO_TRIANGLE_TIGHT_VIEWBOX: string = "140 148 232 216";
